/**
 * Thumbnail Generator Utility
 * 
 * Generates 800x800 max dimension thumbnails client-side using Canvas API
 * Maintains aspect ratio, never enlarges images
 */

/**
 * Generate 800x800 max dimension thumbnail from a File
 */
export async function generateMediumThumbnail(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }
    
    img.onload = () => {
      // Calculate dimensions (max 800px, maintain aspect ratio)
      let width = img.width;
      let height = img.height;
      
      if (width > 800 || height > 800) {
        const ratio = Math.min(800 / width, 800 / height);
        width = width * ratio;
        height = height * ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to generate thumbnail blob'));
            return;
          }
          
          // Create File from blob (keep original filename - thumb_ prefix no longer needed)
          const thumbnailFile = new File(
            [blob],
            file.name,
            { type: 'image/jpeg' }
          );
          
          resolve(thumbnailFile);
        },
        'image/jpeg',
        0.85 // 85% quality
      );
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    // Load image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string;
      }
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Generate thumbnails for multiple files
 */
export async function generateMediumThumbnails(files: File[]): Promise<File[]> {
  const thumbnails = await Promise.all(
    files.map(file => generateMediumThumbnail(file).catch(error => {
      console.warn(`Failed to generate thumbnail for ${file.name}:`, error);
      return file; // Return original file if thumbnail generation fails
    }))
  );
  
  return thumbnails;
}

